<template>
  <section class="accrual-balances" data-testid="profile-accrual-balances">
    <accrual-balance-adjustment-modal
      :visible.sync="adjustmentVisible"
      :associate-id="associateId"
      :is-union="isUnion"
      :balances="balances"
      @saved="handleBalanceSaved"
      @error="handleBalanceError"
    />

    <div class="accrual-balances__header">
      <div>
        <h3 class="m-0">Paid Time Balances</h3>
        <p>Current balances for this associate.</p>
      </div>
      <a-button v-if="canAdjustBalance" type="primary" @click="adjustmentVisible = true"> Adjust Balance </a-button>
    </div>

    <a-spin :spinning="loading">
      <a-alert
        v-if="loadError"
        type="error"
        show-icon
        message="Balances could not be loaded"
        :description="loadError"
      />
      <a-button v-if="loadError" class="mt-2" @click="fetchBalances">Try again</a-button>
      <div v-else-if="balances.length" class="accrual-balances__cards">
        <a-card v-for="balance in balances" :key="balance.id" size="small" class="accrual-balances__card">
          <h4>{{ categoryLabel(balance.category) }}</h4>
          <div class="accrual-balances__available">{{ formatHours(balance.available) }} <span>available</span></div>
          <dl>
            <div>
              <dt>Current</dt>
              <dd>{{ formatHours(balance.current) }}</dd>
            </div>
            <div>
              <dt>Reserved</dt>
              <dd>{{ formatHours(balance.reserved) }}</dd>
            </div>
          </dl>
        </a-card>
      </div>
      <a-empty v-else description="No paid time balances are available for this associate." />
    </a-spin>
  </section>
</template>

<script>
import permissionMixin from '@/mixins/permissionMixin'
import util from '@/util'
import { Alert, Button, Card, Empty, notification, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import AccrualBalanceAdjustmentModal from './accrual-balance-adjustment-modal.vue'

const { mapActions } = createNamespacedHelpers('accrual')

export default {
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-card': Card,
    'a-empty': Empty,
    'a-spin': Spin,
    'accrual-balance-adjustment-modal': AccrualBalanceAdjustmentModal
  },
  mixins: [permissionMixin],
  props: {
    associateId: {
      type: [Number, String],
      default: undefined
    },
    isUnion: {
      type: Boolean,
      default: null
    }
  },
  data() {
    return {
      balances: [],
      loading: false,
      loadError: '',
      adjustmentVisible: false
    }
  },
  computed: {
    canAdjustBalance() {
      return this.hasAnyPermission(['validate_payments', 'process_payments'])
    }
  },
  watch: {
    associateId: {
      handler() {
        this.fetchBalances()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['getAccrualBalances']),
    categoryLabel(category) {
      return { sick: 'Sick', vacation: 'Vacation', time_off: 'Time Off' }[category] ?? category
    },
    formatHours(value) {
      const numericValue = Number(value ?? 0)

      return util.formatMinutesToHoursAndMinutesDuration(Number.isFinite(numericValue) ? Math.round(numericValue * 60) : 0)
    },
    showNotification(type, message, description) {
      notification[type]({ message, description })
    },
    async fetchBalances() {
      if (!this.associateId) return

      const associateId = this.associateId

      this.balances = []
      this.loading = true

      try {
        const balances = await this.getAccrualBalances({ associate_id: associateId })

        if (this.associateId !== associateId) return

        this.balances = balances
        this.loadError = ''
      } catch (error) {
        if (this.associateId !== associateId) return

        this.loadError = error?.detail || error?.msg || error?.message || String(error)
      } finally {
        if (this.associateId === associateId) this.loading = false
      }
    },
    async handleBalanceSaved() {
      await this.fetchBalances()
      this.showNotification('success', 'Success', 'Balance updated successfully.')
    },
    async handleBalanceError(error) {
      await this.fetchBalances()
      this.showNotification(
        'error',
        'Error',
        error?.detail || error?.msg || error?.message || 'Balance could not be updated.'
      )
    }
  }
}
</script>

<style scoped>
.accrual-balances {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.accrual-balances__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.accrual-balances__header p,
.accrual-balances__card h4 {
  margin: 0;
}

.accrual-balances__header p {
  margin-top: 4px;
  color: var(--text-color-secondary);
}

.accrual-balances__cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.accrual-balances__available {
  margin-top: 12px;
  font-size: 24px;
  font-weight: 700;
}

.accrual-balances__available span {
  color: var(--text-color-secondary);
  font-size: 12px;
  font-weight: 500;
}

.accrual-balances__card dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 12px 0 0;
}

.accrual-balances__card dl div {
  min-width: 0;
}

.accrual-balances__card dt {
  color: var(--text-color-secondary);
  font-size: 12px;
}

.accrual-balances__card dd {
  margin: 2px 0 0;
  font-weight: 600;
}

@media (max-width: 520px) {
  .accrual-balances__header {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
